const GRAPH_OPTIONAL_STEP_TYPES = new Set(['trigger', 'input']);

const isObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

export const shouldHaveWorkflowGraphNode = (step) => {
  return !!step?.id && !GRAPH_OPTIONAL_STEP_TYPES.has(step?.type);
};

const addError = (errors, code, message, details = {}) => {
  errors.push({ code, message, ...details });
};

const getNodeReferences = (node) => {
  const references = [
    { relation: 'exit', stepId: node?.exit ?? null },
    { relation: 'child', stepId: node?.child ?? null }
  ];

  if (Array.isArray(node?.branches)) {
    node.branches.forEach((stepId, index) => {
      references.push({ relation: 'branch', index, stepId: stepId ?? null });
    });
  }

  return references;
};

const validateReference = ({ errors, graphStepIds, sourceStepId, reference }) => {
  const { stepId, relation, index } = reference;
  if (stepId === null) {
    return null;
  }

  if (typeof stepId !== 'string' || stepId.length === 0) {
    addError(errors, 'invalid_graph_reference', `Step ${sourceStepId} has an invalid ${relation} reference.`, {
      sourceStepId,
      relation,
      index,
      value: stepId
    });
    return null;
  }

  if (!graphStepIds.has(stepId)) {
    addError(errors, 'missing_graph_reference', `Step ${sourceStepId} references missing graph step ${stepId}.`, {
      sourceStepId,
      relation,
      index,
      targetStepId: stepId
    });
    return null;
  }

  return stepId;
};

const collectReachableStepIds = (graph, rootStepId) => {
  const reachable = new Set();
  const visit = (stepId) => {
    if (!stepId || reachable.has(stepId) || !graph?.[stepId]) {
      return;
    }

    reachable.add(stepId);
    for (const reference of getNodeReferences(graph[stepId])) {
      visit(reference.stepId);
    }
  };

  visit(rootStepId);
  return reachable;
};

const detectCycles = ({ graph, errors }) => {
  const visiting = new Set();
  const visited = new Set();

  const visit = (stepId, path = []) => {
    if (!graph?.[stepId]) {
      return;
    }

    if (visiting.has(stepId)) {
      addError(errors, 'cycle', `Workflow graph contains a cycle at step ${stepId}.`, {
        stepId,
        path: [...path, stepId]
      });
      return;
    }

    if (visited.has(stepId)) {
      return;
    }

    visiting.add(stepId);
    for (const reference of getNodeReferences(graph[stepId])) {
      if (reference.stepId && graph?.[reference.stepId]) {
        visit(reference.stepId, [...path, stepId]);
      }
    }
    visiting.delete(stepId);
    visited.add(stepId);
  };

  for (const stepId of Object.keys(graph || {})) {
    visit(stepId);
  }
};

export const validateWorkflowGraphIntegrity = ({ graph = {}, steps = [] } = {}) => {
  const errors = [];
  // Non-blocking; `valid` ignores these.
  const warnings = [];

  if (!isObject(graph)) {
    return {
      valid: false,
      errors: [{ code: 'invalid_graph', message: 'Workflow graph must be an object.' }],
      warnings
    };
  }

  const stepById = new Map((steps || []).filter((step) => !!step?.id).map((step) => [step.id, step]));
  const graphStepIds = new Set(Object.keys(graph || {}));
  const incomingReferences = new Map();
  const malformedNodeIds = new Set();

  for (const stepId of graphStepIds) {
    const node = graph[stepId];
    if (!stepById.has(stepId)) {
      addError(errors, 'graph_step_missing_row', `Graph step ${stepId} does not have a matching workflow step row.`, { stepId });
    }

    if (!isObject(node)) {
      malformedNodeIds.add(stepId);
      addError(errors, 'invalid_graph_node', `Graph step ${stepId} must be an object.`, { stepId });
      continue;
    }

    if (node.branches !== undefined && !Array.isArray(node.branches)) {
      malformedNodeIds.add(stepId);
      addError(errors, 'invalid_branches', `Graph step ${stepId} branches must be an array.`, { stepId });
      continue;
    }

    if (Array.isArray(node.branches) && node.branches.length > 2) {
      addError(errors, 'too_many_branches', `Graph step ${stepId} has ${node.branches.length} branch lanes; the maximum is 2.`, {
        stepId,
        observed: node.branches.length,
        max: 2
      });
      // Intentionally NO `continue` (do not "consistency-fix" this to match invalid_branches above):
      // the lane heads are still valid string refs in graphStepIds, so falling through the reference
      // walk gives each exactly one parent and keeps the node reachable — emitting too_many_branches
      // ALONE. A `continue` would skip the walk, strand the lane heads with no parent, and cascade a
      // spurious multiple_roots on top (verified: reachability still holds via collectReachableStepIds,
      // so unreachable_graph_step does not fire — but multiple_roots alone is already a wrong extra error).
    }

    for (const reference of getNodeReferences(node)) {
      const targetStepId = validateReference({ errors, graphStepIds, sourceStepId: stepId, reference });
      if (!targetStepId) {
        continue;
      }

      const incoming = incomingReferences.get(targetStepId) || [];
      incoming.push({ sourceStepId: stepId, relation: reference.relation, index: reference.index });
      incomingReferences.set(targetStepId, incoming);
    }
  }

  for (const step of steps || []) {
    if (shouldHaveWorkflowGraphNode(step) && !graphStepIds.has(step.id)) {
      // Orphan action row: benign (never runs, invisible) — warn, don't reject. GRA-18377.
      addError(warnings, 'step_missing_graph_node', `Workflow step ${step.id} does not have a matching graph node.`, { stepId: step.id });
    }
  }

  for (const [stepId, incoming] of incomingReferences.entries()) {
    if (incoming.length > 1) {
      addError(errors, 'multiple_parents', `Graph step ${stepId} has multiple parents.`, { stepId, incoming });
    }
  }

  const validGraphStepIds = [...graphStepIds].filter((stepId) => !malformedNodeIds.has(stepId));
  if (validGraphStepIds.length > 0) {
    const rootStepIds = validGraphStepIds.filter((stepId) => (incomingReferences.get(stepId) || []).length === 0);
    if (rootStepIds.length === 0) {
      addError(errors, 'missing_root', 'Workflow graph must have one reachable root step.', { rootStepIds });
    } else {
      if (rootStepIds.length > 1) {
        addError(errors, 'multiple_roots', 'Workflow graph must have one root step.', { rootStepIds });
      }

      const reachableStepIds = collectReachableStepIds(graph, rootStepIds[0]);
      for (const stepId of validGraphStepIds) {
        if (!reachableStepIds.has(stepId)) {
          addError(errors, 'unreachable_graph_step', `Graph step ${stepId} is not reachable from the workflow root.`, { stepId, rootStepId: rootStepIds[0] });
        }
      }
    }
  }

  detectCycles({ graph, errors });

  return { valid: errors.length === 0, errors, warnings };
};
